import { useEffect, useRef, useState } from 'react'
import { Route, Routes, Link, useLocation } from 'react-router'
import { Button, LangToggle, MenuPanel, Notice, Spinner, useLang } from '@ksa/ui'
import { getTemplate } from '@ksa/render'
import { api } from './api.ts'
import { useLoad } from './useLoad.ts'
import { safeToReload, useDeployWatch } from './useDeployWatch.ts'
import { EditorChromeProvider, useEditorChrome } from './editorChrome.tsx'
import { Landing } from './pages/Landing.tsx'
import { Sites } from './pages/Sites.tsx'
import { DemoEditor, DraftEditor, Editor } from './pages/Editor.tsx'
import { Pay } from './pages/Pay.tsx'
import { Report } from './pages/Report.tsx'

export function App() {
  const [session] = useLoad((signal) => api.session(signal), [])

  // ⚠️ AT THE SHELL, SO IT SURVIVES EVERY NAVIGATION. Put on a page, it would restart its polling
  // and forget which build it had seen each time somebody moved between screens.
  useDeployWatch(safeToReload)
  const signedIn = session.state === 'ready' && session.data.signedIn

  return (
    <EditorChromeProvider>
    <div className="u-shell">
      <a className="u-sr u-sr-focus" href="#main">
        Skip to content
      </a>
      <TopBar signedIn={signedIn} loading={session.state === 'loading'} />
      <main id="main" className="u-main">
        <SignupOutcome />
        <Routes>
          <Route path="/" element={<Landing signedIn={signedIn} />} />
          <Route path="/sites" element={<Sites />} />
          <Route path="/s/:id" element={<Editor />} />
          {/*
            One of the landing page's examples, in the real editor, owned by nobody and saved nowhere.
            No session is required and none is asked for — see `DemoEditor`.
          */}
          <Route path="/demo/:sample" element={<DemoEditor signedIn={signedIn} />} />
          {/*
            A site the model wrote for somebody with no account. Same workspace, same drawer, same
            "not saved" bar — the only difference from an example is where the document came from,
            and it lives in this tab until they keep it. See `DraftEditor`.
          */}
          <Route path="/draft/:draft" element={<DraftEditor signedIn={signedIn} />} />
          <Route path="/pay" element={<Pay />} />
          {/* Linked from the footer of every published customer site. No sign-in: the person who
              needs it is a stranger who landed on a site, not a customer of ours. */}
          <Route path="/report" element={<Report />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>
      <SiteFooter />
    </div>
    </EditorChromeProvider>
  )
}

/**
 * The header.
 *
 * ⚠️ THE WORDMARK IS GONE, DELIBERATELY. It cost 113 px of a 360 px header to tell a signed-in
 * customer the name of the tool they are already inside — and it was the widest thing up there, so
 * it was what pushed the account links onto a third row. What a customer needs in that space is
 * WHICH SITE THEY ARE EDITING, which the product knows and was not saying. The name still leads the
 * marketing page, where somebody who does not know it is actually reading.
 *
 * ⚠️ THE SITE MENU IS ON THE INLINE START AND SIGN-OUT ON THE INLINE END, which mirrors under RTL
 * without either of them naming a side.
 */
/**
 * ⚠️ IT HIDES ON THE WAY DOWN AND COMES BACK ON THE WAY UP. A sticky header costs its own height on
 * every screen for ever, and on a phone that is a tenth of the viewport permanently spent on controls
 * somebody is not using while they read. Hiding it while they scroll AWAY and returning it the
 * instant they scroll BACK is the arrangement where the height is only spent when it is wanted —
 * reaching for the header is itself an upward scroll.
 *
 * ⚠️ IT NEVER HIDES NEAR THE TOP, so the first thing on the page is never a header that is halfway
 * out of the way, and a short page cannot end up with a header stuck off-screen.
 *
 * ⚠️ AND IT TRANSFORMS RATHER THAN CHANGING `position`. Toggling sticky/fixed reflows the document
 * and makes the content jump under the reader's thumb; a transform moves only the painted layer.
 */
function useAutoHide(): boolean {
  const [hidden, setHidden] = useState(false)
  const last = useRef(0)

  useEffect(() => {
    // A little slack, or a trackpad's sub-pixel wobble flickers the header.
    const SLACK = 8
    const ALWAYS_SHOW_ABOVE = 80

    const onScroll = () => {
      const y = window.scrollY
      const delta = y - last.current
      if (Math.abs(delta) < SLACK) return
      last.current = y
      setHidden(y > ALWAYS_SHOW_ABOVE && delta > 0)
    }

    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  return hidden
}

function TopBar({ signedIn, loading }: { signedIn: boolean; loading: boolean }) {
  const { t } = useLang()
  const location = useLocation()
  const chrome = useEditorChrome()
  // ⚠️ THE DEMONSTRATION COUNTS AS THE EDITOR. It renders the same workspace and lends the header the
  // same controls; a route check that knew about only one of the two would give it a site menu it
  // cannot use and no way back.
  const inEditor = /^\/(s|demo|draft)\//.test(location.pathname)
  const hidden = useAutoHide()

  return (
    <header className={`u-topbar ${hidden ? 'u-topbar--away' : ''}`}>
      {/*
        ⚠️ INSIDE THE EDITOR THIS IS A BACK ARROW, NOT THE SITE MENU. A customer editing a site does
        not want a list of their other sites in the corner — they want out, and "out" is one place.
        The menu returns on every other screen, where choosing between sites is the actual job.
      */}
      {inEditor ? <BackTo where={signedIn ? '/sites' : '/'} /> : signedIn ? <SiteMenu /> : (
        <Link to="/" className="u-brand">ksa-web<span className="u-brand__dot">.</span>app</Link>
      )}

      {/* Everything the editor's two bars used to hold. */}
      <EditorMenu signedIn={signedIn} />
      <ThemeSquare />
      <KeepButton />
      <PublishButton />

      <div className="u-spacer" />
      <LangToggle />
      {loading ? (
        <Spinner />
      ) : signedIn && !inEditor ? (
        /* A plain link, because signing out is a navigation and there is nothing here worth a token:
           the worst an attacker achieves is signing somebody out. */
        <a href="/auth/signout" className="u-btn u-btn--ghost u-btn--sm">
          {t('Sign out', 'خروج')}
        </a>
      ) : signedIn ? (
        /*
         * ⚠️ SIGNING OUT MOVED INTO THE MENU INSIDE THE EDITOR, TO MAKE ROOM FOR «PUBLISH». Measured
         * at 360 px in both languages: a back arrow, the pane menu, the colour square, Publish, the
         * language pill and Sign out is 109 px of sticky header — two rows, a seventh of the screen,
         * on the one screen whose whole job is showing somebody their website.
         *
         * Of those six, signing out is the one nobody does from inside a site they are editing, and
         * it is the only one that is about the ACCOUNT rather than about this site. It is one tap
         * away in the menu that already holds Saved and Undo, and the back arrow goes to the sites
         * list where it sits in the header as usual.
         */
        null
      ) : chrome?.keep ? (
        /*
         * ⚠️ NOTHING HERE: "KEEP THIS SITE" IS THE SIGN-IN, AND TWO BUTTONS FOR ONE ACT IS ONE TOO
         * MANY. A signed-out visitor looking at a site that belongs to nobody has exactly one thing
         * worth doing, and pressing Keep signs them in on the way to doing it. Measured at 360 px,
         * carrying both pushed this header onto a second row — a fifth of the screen, sticky, on the
         * one screen whose whole job is showing somebody their website.
         */
        null
      ) : (
        /*
          ⚠️ TWO CONTROLS HERE, WHICH THE NOTE ABOVE ARGUES AGAINST — AND THE DIFFERENCE IS THAT ONE
          OF THESE WAS MISSING ENTIRELY. The argument above is about not carrying a SECOND way to do
          the SAME act; these are two different acts, and a person with no passkey could not perform
          theirs at all. Signing in offers `credentials.get()`, which shows a QR code to another
          device or a hardware key and never offers to create a passkey — so "Sign in" alone is a
          door with no handle for everybody who is not already a customer.

          ⚠️ SIGN-UP IS THE QUIET ONE AND SIGN-IN IS THE BUTTON, which is the opposite of what a
          growth instinct suggests and is right for this header: it is shown on every screen a
          signed-out person sees, including the ones a returning customer lands on, and they
          outnumber new arrivals on it. The marketing page's own call to action is where somebody is
          being ASKED to join.

          ⚠️ AND `returnTo` IS WHERE THEY ARE, NOT `/sites`. A sign-up that cannot be minted comes
          back here carrying `?signup=`, and bouncing them to a page they cannot see yet would turn
          one refusal into two.
        */
        <>
          <a className="u-btn u-btn--ghost u-btn--sm" href={`/auth/signup?returnTo=${encodeURIComponent(location.pathname)}`}>
            {t('Sign up', 'انضم')}
          </a>
          <a
            className="u-btn u-btn--sm"
            href={`/auth/start?returnTo=${encodeURIComponent(location.pathname === '/' ? '/sites' : location.pathname)}`}
          >
            {/*
              ⚠️ THE ARABIC IS «انضم» AND «دخول» RATHER THAN «حساب جديد» AND «تسجيل الدخول», AND THE
              REASON IS A MEASUREMENT RATHER THAN A PREFERENCE. Two controls at the long forms put
              this header on two sticky rows at 360 px — 94 px against English's 52 — which is the
              Arabic-first failure this codebase keeps paying for (§7). Measured piece by piece:
              wordmark 113, language 38, and the two long labels 95 and 95 against about 328 px of
              usable width once the padding is taken off.

              ⚠️ AND SHORTENING ONE OF THEM WAS NOT ENOUGH, WHICH IS WHY BOTH MOVED. The first
              attempt changed only «تسجيل الدخول» and measured 94 px again — unchanged — because the
              overflow was the pair, not either one. Both short forms are what Arabic interfaces
              actually use for these two controls, so nothing is lost but the width.
            */}
            {t('Sign in', 'دخول')}
          </a>
        </>
      )}
    </header>
  )
}

/**
 * What happened when somebody pressed «Sign up» and it did not work.
 *
 * ⚠️ **THE ROUTE ANSWERS WITH A REDIRECT CARRYING A REASON, AND THIS IS THE HALF THAT SPEAKS.** A 503
 * from `/auth/signup` would be a white page on the one button a stranger pressed to join the
 * product; the app knows both languages and the route does not, so the refusal travels as
 * `?signup=` and is said here.
 *
 * ⚠️ AND IT SITS AT THE SHELL RATHER THAN ON A PAGE, because `returnTo` is wherever they were — the
 * marketing page, a demonstration, the sites list — and a notice that only existed on one of them
 * would be silence on the others.
 *
 * ⚠️ IT OFFERS SIGNING IN, WHICH LOOKS LIKE A NON-SEQUITUR AND IS THE MOST USEFUL THING ON IT. The
 * commonest reason a person presses Sign up is not knowing which one they need, and somebody who
 * already has a passkey is one press from being through. The failure is ours; the way past it may
 * not be.
 */
function SignupOutcome() {
  const { t } = useLang()
  const location = useLocation()
  const why = new URLSearchParams(location.search).get('signup')
  if (why !== 'failed' && why !== 'busy') return null

  return (
    <Notice tone="warn" title={t('We could not start a new account', 'تعذّر بدء حساب جديد')}>
      <p className="u-small">
        {why === 'busy'
          ? t(
              'A lot of people are joining at once. Try again in a few minutes.',
              'هناك إقبال كبير الآن. حاول مرة أخرى بعد بضع دقائق.',
            )
          : t(
              'Something on our side did not answer. Nothing was created, so trying again is safe.',
              'لم يستجب شيء لدينا. لم يُنشأ أي شيء، لذا المحاولة مرة أخرى آمنة.',
            )}
      </p>
      <p className="u-small" style={{ marginBlockStart: 'var(--u-s2)' }}>
        <a href="/auth/start">{t('Already have a passkey? Sign in', 'لديك مفتاح مرور؟ سجّل الدخول')}</a>
      </p>
    </Notice>
  )
}

/**
 * "My sites", and which one you are in.
 *
 * ⚠️ IT READS THE SITE ID OUT OF THE PATH RATHER THAN FROM A CONTEXT. The header sits OUTSIDE
 * `<Routes>`, so `useParams` gives it nothing; a context provider spanning the whole app so one
 * label can change is more machinery than the label is worth. The path is the source of truth for
 * which site is open, and it is already there.
 *
 * ⚠️ THE LIST IS LOADED ONCE, WHEN SIGNED IN, AND SHARED WITH NOTHING. That is a second request for
 * the same data the sites page fetches, and it is accepted: the alternative is a cache that has to
 * be invalidated when a site is renamed, created or deleted, and a stale name in the header is a
 * worse bug than one extra request on a page load.
 */
function SiteMenu() {
  const { t, lang } = useLang()
  const location = useLocation()
  const [open, setOpen] = useState(false)
  const trigger = useRef<HTMLButtonElement>(null)
  const [sites] = useLoad((signal) => api.sites(signal), [])

  const currentId = /^\/s\/([^/]+)/.exec(location.pathname)?.[1]
  const list = sites.state === 'ready' ? sites.data : []
  const current = list.find((site) => site.id === currentId)

  // Close on navigation — a menu that survives the click that used it feels broken.
  useEffect(() => setOpen(false), [location.pathname])

  return (
    <div className="u-menu">
      <button
        ref={trigger}
        type="button"
        className="u-btn u-btn--ghost u-btn--sm u-menu__button"
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={() => setOpen((o) => !o)}
      >
        <span className="u-menu__label">{current ? current.name : t('My sites', 'مواقعي')}</span>
        <span aria-hidden="true">▾</span>
      </button>

      {open && (
        /*
         * ⚠️ THE PANEL IS PORTALLED, AND THIS MENU IS WHY IT HAD TO BE. Its backdrop was written out
         * here — `position: fixed`, `inset: 0`, the whole viewport — and it measured 412×56, because
         * the header carries `will-change: transform` and that makes it the containing block for its
         * fixed descendants. A tap anywhere below the header never reached it and the menu would not
         * close. See `MenuPanel`.
         */
        <MenuPanel anchor={trigger} onClose={() => setOpen(false)}>
            <Link to="/sites" role="menuitem" className="u-menu__item">
              {t('All my sites', 'كل مواقعي')}
            </Link>
            {list.length > 0 && <div className="u-menu__rule" />}
            {list.map((site) => (
              <Link
                key={site.id}
                to={`/s/${site.id}`}
                role="menuitem"
                className={`u-menu__item ${site.id === currentId ? 'u-menu__item--current' : ''}`}
              >
                <span>{site.name}</span>
                <span className="u-num u-tiny u-faint">{site.handle}</span>
              </Link>
            ))}
            {sites.state === 'loading' && (
              <span className="u-menu__item u-faint">{t('Loading…', 'جارٍ التحميل…')}</span>
            )}
        </MenuPanel>
      )}
      <span className="u-sr">{lang === 'ar' ? 'قائمة المواقع' : 'Site menu'}</span>
    </div>
  )
}

function SiteFooter() {
  const { t } = useLang()
  return (
    <footer style={{ borderBlockStart: '1px solid var(--u-line)', padding: 'var(--u-s5)' }}>
      <div className="u-page u-row u-row--between">
        <span className="u-tiny u-faint">
          {t('ksa-web.app — a website for your business.', 'ksa-web.app — موقع لنشاطك التجاري.')}
        </span>
        <span className="u-tiny u-faint">
          {/*
            ⚠️ THE VAT LINE IS NOT A DESIGN CHOICE. A displayed consumer price in Saudi Arabia must
            state that it includes VAT; see pricing.ts. It lives in the footer as well as beside
            every price so it cannot be lost by a redesign of a single card.
          */}
          {t('All prices include 15% VAT.', 'جميع الأسعار شاملة ضريبة القيمة المضافة ١٥٪.')}
        </span>
      </div>
    </footer>
  )
}

function NotFound() {
  const { t } = useLang()
  return (
    <div className="u-page u-section u-stack" style={{ textAlign: 'center' }}>
      <h1 className="u-title">{t('That page is not here', 'هذه الصفحة غير موجودة')}</h1>
      <div className="u-row" style={{ justifyContent: 'center' }}>
        <Link to="/">
          <Button>{t('Go to the start', 'العودة إلى البداية')}</Button>
        </Link>
      </div>
    </div>
  )
}

/**
 * ⚠️ AN SVG, NOT AN ARROW CHARACTER. `←` is a glyph whose direction is decided by the font and the
 * bidi algorithm, so in an Arabic interface it points the wrong way about half the time depending on
 * what surrounds it. A drawn path is mirrored deliberately, by the stylesheet, from the writing mode.
 */
/**
 * ⚠️ IT DOES NOT ALWAYS POINT AT `/sites`, AND THAT IS NEW. A signed-out visitor looking at one of the
 * examples is in the editor too — and `/sites` needs a session, so the way OUT of the demonstration
 * was a sign-in redirect. Out, for them, is the page they came from.
 */
function BackTo({ where }: { where: string }) {
  const { t } = useLang()
  return (
    <Link
      to={where}
      className="u-iconbtn"
      aria-label={where === '/sites' ? t('Back to your sites', 'العودة إلى مواقعك') : t('Back to the start', 'العودة إلى البداية')}
    >
      <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false">
        <path
          d="M15 5 8 12l7 7"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </Link>
  )
}

/**
 * "Keep this site" — the whole truth about an unkept site, in one control.
 *
 * ⚠️ IT IS PRESENT EXACTLY WHILE THE SITE BELONGS TO NOBODY, AND THAT IS THE POINT OF IT. The arrival
 * dialog can be dismissed, so it cannot be the only place the product says a site is saved nowhere —
 * somebody closes it, edits for ten minutes and shuts the tab. A button that says *Keep this site*
 * makes the same claim by existing, and stops making it the moment it stops being true, without
 * spending a strip of a 360 px screen on a banner nobody re-reads (CLAUDE.md §0.4).
 *
 * ⚠️ AND IT IS NOT AN ALARM. It is an ordinary button in the row of editor controls, because for most
 * of the time somebody spends here the fact is not urgent — it is simply true.
 */
/**
 * The one thing this product asks to be paid for, in the header, on every site that can have it.
 *
 * ⚠️ IT SHARES A SLOT WITH `Keep this site` AND THAT IS THE WHOLE DESIGN. The header carries the next
 * thing this site needs and never two things: Keep while it belongs to nobody, Publish once it does.
 * Both are the same act from the customer's side — "make this real" — and both disappear when they
 * stop being true, which is what keeps the header from growing a second row at 360 px.
 *
 * ⚠️ IT OPENS THE WIZARD, IT DOES NOT PUBLISH. What is in there — the readiness checks, the address
 * nobody has chosen yet, and what happens when the free week ends — is the part somebody has to
 * read, and a header button that skipped it would put a site on the internet on one tap.
 */
function PublishButton() {
  const { t } = useLang()
  const chrome = useEditorChrome()
  if (!chrome?.publishable) return null

  /*
   * ⚠️ A SITE THAT IS ALREADY LIVE AND UNCHANGED IS OFFERED «OPEN», NOT «PUBLISH». The button carries
   * the next thing this site needs (the same rule that makes it share a slot with «Keep this site»),
   * and for a site whose snapshot is this document, that is not another publish — it is the first
   * look at the thing they have been paying for. Offering «Publish» there invites a customer to
   * re-run a ceremony that changes nothing, which reads as "it did not work the first time".
   *
   * ⚠️ AND IT IS AN ANCHOR RATHER THAN A BUTTON, because it leaves this application for the
   * customer's own address. `target="_blank"` deliberately: the editor is a document with unsaved
   * state and navigating away from it to look at the live page is a good way to lose an edit.
   */
  if (chrome.liveUrl) {
    return (
      <a className="u-btn u-btn--sm" href={chrome.liveUrl} target="_blank" rel="noopener noreferrer">
        {t('Open', 'افتح')}
      </a>
    )
  }

  return (
    <Button size="sm" onClick={() => chrome.openPublish?.()}>
      {t('Publish', 'انشر')}
    </Button>
  )
}

function KeepButton() {
  const { t } = useLang()
  const chrome = useEditorChrome()
  if (!chrome?.keep) return null

  return (
    /*
     * ⚠️ THE LABEL SHEDS ITS SECOND HALF ON A PHONE, AND THE ACCESSIBLE NAME KEEPS IT. Measured at
     * 390 px: "Keep this site" beside the pane menu, the colour square, the language pill and
     * "Sign in" pushed this header onto a second row — a fifth of the screen, sticky, on the one
     * screen that is supposed to be showing somebody their website. Shortening what is PAINTED while
     * `aria-label` carries the whole sentence is the same trade the language toggle already makes
     * («ع» rather than «العربية»), and nothing is lost to anybody who cannot see the button.
     */
    <Button
      size="sm"
      busy={chrome.keep.busy}
      aria-label={t('Keep this site', 'احتفظ بهذا الموقع')}
      onClick={chrome.keep.onKeep}
    >
      {t('Keep', 'احتفظ')}
      <span className="u-wide-only">{t(' this site', ' بهذا الموقع')}</span>
    </Button>
  )
}

/**
 * The colour scheme, as a square of the colours.
 *
 * ⚠️ IT OPENS THE DRAWER RATHER THAN A MENU OF ITS OWN, AND THAT IS THE POINT. A palette in a dropdown
 * hangs over the page it is repainting, so every choice is judged against the two centimetres of site
 * left visible around the menu. The drawer takes half the screen at most and the other half is the
 * customer's own page changing colour under their thumb — see `ThemePicker`.
 *
 * ⚠️ IT SHOWS THE CURRENT SCHEME RATHER THAN A PAINT ICON, so the control is also the answer to
 * "which one am I on". Two colours, because `brand` alone is nearly black in two of the five
 * templates and those two would be indistinguishable.
 */
function ThemeSquare() {
  const { t } = useLang()
  const chrome = useEditorChrome()
  if (!chrome) return null

  const template = getTemplate(chrome.template)

  return (
    <button
      type="button"
      className="u-swatchbtn"
      aria-pressed={chrome.theme}
      aria-label={t('Colours', 'الألوان')}
      title={t('Colours', 'الألوان')}
      onClick={() => chrome.setTheme(!chrome.theme)}
    >
      <span aria-hidden="true" style={{ background: template.colors.brand }} />
      <span aria-hidden="true" style={{ background: template.colors.accent }} />
    </button>
  )
}

/**
 * The editor's controls, as one menu.
 *
 * ⚠️ IT RENDERS NOTHING OUTSIDE THE EDITOR, because `useEditorChrome()` is `null` there. That is why
 * it can sit unconditionally in the header rather than behind a route check that would have to be
 * kept in step with the router.
 *
 * ⚠️ THE SAVE STATE IS IN HERE *AND* THE BUTTON SHOWS IT. "Saving…" that is only visible after
 * opening a menu is not an indicator, and §0.4 puts that claim on whatever knows — so an unsaved dot
 * sits on the button itself, and the wording is inside.
 */
function EditorMenu({ signedIn }: { signedIn: boolean }) {
  const { t } = useLang()
  const chrome = useEditorChrome()
  const [open, setOpen] = useState(false)
  const trigger = useRef<HTMLButtonElement>(null)
  if (!chrome) return null

  const paneLabel: Record<string, string> = {
    rail: t('Pages', 'الصفحات'),
    stage: t('Preview', 'المعاينة'),
  }

  const choose = (pane: 'rail' | 'stage') => {
    chrome.setPane(pane)
    setOpen(false)
  }

  return (
    <div className="u-menu">
      <button
        ref={trigger}
        type="button"
        className="u-btn u-btn--ghost u-btn--sm u-menu__button"
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={() => setOpen((o) => !o)}
      >
        <span className="u-menu__label">{paneLabel[chrome.pane]}</span>
        {/*
          ⚠️ THE DOT IS FOR A FAILED SAVE ONLY, NOT FOR "SAVING" OR "DIRTY". A marker that appears on
          every keystroke is one nobody reads by the second minute, and it would be the third thing
          this header has tried to say about saving. This one means something has actually gone
          wrong, so it is rare enough to be worth looking at — the words are one tap away, inside.
        */}
        {chrome.saving === 'failed' && <span className="u-dot u-dot--bad" aria-hidden="true" />}
        <span aria-hidden="true">▾</span>
      </button>

      {open && (
        <MenuPanel anchor={trigger} onClose={() => setOpen(false)}>
            {(['rail', 'stage'] as const).map((pane) => (
              <button
                key={pane}
                type="button"
                role="menuitem"
                className={`u-menu__item ${chrome.pane === pane ? 'u-menu__item--current' : ''}`}
                onClick={() => choose(pane)}
              >
                {paneLabel[pane]}
              </button>
            ))}

            {/*
              ⚠️ THE ONE THING THIS PRODUCT ASKS TO BE PAID FOR HAD NO NAME ANYWHERE IN THE CHROME.
              Publishing is the last group of the rail, and on a phone the rail is a pane you reach
              through a menu whose label says «Preview» — so "how do I put this on the internet" was
              two taps and a scroll past four other groups, with nothing on the way saying the word.
              This is a navigation item like the panes above it: it says where the thing is and takes
              you there. What is ON the panel — the checks, the address, the price — stays there,
              because that is the component that knows.
            */}
            <button
              type="button"
              role="menuitem"
              className="u-menu__item"
              onClick={() => {
                setOpen(false)
                chrome.openPublish?.()
              }}
            >
              {t('Publish', 'انشر')}
            </button>

            <div className="u-menu__rule" />

            <button
              type="button"
              role="menuitem"
              className="u-menu__item"
              onClick={() => {
                chrome.setFull(!chrome.full)
                setOpen(false)
              }}
            >
              {chrome.full ? t('Exit full screen', 'إنهاء ملء الشاشة') : t('Full screen', 'ملء الشاشة')}
            </button>

            <button
              type="button"
              role="menuitem"
              className="u-menu__item"
              disabled={!chrome.canUndo}
              onClick={() => {
                chrome.undo()
                setOpen(false)
              }}
            >
              {t('Undo', 'تراجع')}
            </button>

            {/*
              > ⚠️ **A SECOND LANGUAGE TOGGLE STOOD HERE — THE PREVIEW'S — AND IT IS GONE.** Changed
              > on request. It set which language of the CUSTOMER'S SITE was on screen, independently
              > of which language the BUILDER spoke, and the reasoning was sound: somebody who does
              > not read Arabic can work on an Arabic page. What it cost is that the two could
              > disagree, and every field then had to show both languages at once so the editor could
              > not lie about which one you were changing. One toggle in the header moves both now,
              > which is what makes a one-language-at-a-time field honest. See `fields.tsx`.
            */}
            <div className="u-menu__rule" />

            {/*
              ⚠️ SAVING IS REPORTED HERE AND NOWHERE ELSE IN THE CHROME. It had a label in the header
              for one commit and it was removed on request — the header is for where you ARE, not for
              a running commentary on a save that succeeds every time. What must not be silent is a
              save that FAILED, which is why the button outside wears a dot for that one state.
            */}
            <span className="u-menu__item u-faint" aria-live="polite">
              {chrome.saving === 'failed'
                ? t('Not saved — check your connection', 'لم يُحفظ — تحقق من اتصالك')
                : chrome.saving === 'clean'
                  ? t('Saved', 'محفوظ')
                  : t('Saving…', 'جارٍ الحفظ…')}
            </span>

            {/* Displaced from the header by «Publish» — see the note there. */}
            {signedIn && (
              <>
                <div className="u-menu__rule" />
                <a href="/auth/signout" role="menuitem" className="u-menu__item">
                  {t('Sign out', 'تسجيل الخروج')}
                </a>
              </>
            )}
        </MenuPanel>
      )}
    </div>
  )
}
